import {
  AccountInfo,
  PublicClientApplication,
  RedirectRequest,
} from "@azure/msal-browser";
import { AuthConfig, getScopesByConfig } from "@granular/fabric3-definitions";
import { type NavigateFunction } from "react-router-dom";

export const POST_LOGIN_REDIRECT = "sso-spa-post-login-redirect";

/**
 * When we visit a path when unauthenticated, we store that path to resume after login. Typically
 * that resume is done with react router. However, some paths lead to pages hosted outside of the
 * SPA, so we'll need to do a full browser redirect to resume a path there. These should be few and
 * far between so let's just hardcode for now.
 */
const FULL_REDIRECT_PATHS = [
  // Start of string, optional leading slash, "tassel", nothing alphanumeric after tassel.
  // (e.g. /tassel, tassel, tassel?..., tassel#..., tassel/... but not /tasseling)
  // We just want to capture a complete path segment.
  /^[/]?tassel(?:$|[^a-zA-Z0-9])/,
];

/**
 * Send the user to the login page, storing the current path to be resumed after login.
 * @param navigate - result of `useNavigate()`, used to redirect to login.
 * @returns
 */
export function navigateToLogin(navigate: NavigateFunction) {
  const currentPath =
    globalThis.location.pathname +
    globalThis.location.search +
    globalThis.location.hash;
  if (currentPath.includes("/login")) {
    // We're already going to login, don't do anything.
    // This prevents redundant redirects when multiple auth checks fail simultaneously.
    return;
  }

  // Don't store /login paths as the resume path
  const shouldStoreResumePath =
    !currentPath.includes("/login") && currentPath !== "/";

  if (shouldStoreResumePath) {
    console.debug("[MSAL] Redirecting to login, storing path:", currentPath);
    // TODO: We should be using state to carry this, so we don't have to rely on sessionStorage.
    // SessionStorage is not as ephemeral as we would like.
    globalThis.window.sessionStorage.setItem(POST_LOGIN_REDIRECT, currentPath);
  } else {
    console.debug("[MSAL] Redirecting to login without storing path");
  }

  navigate("/login", {
    replace: true,
  });
}

/**
 * We've successfully logged in and are sitting at the root.
 * Check if we have a post-login path to resume.
 * TODO: use state instead of sessionStorage
 */
export const doPostLoginRedirect = (navigate: NavigateFunction) => {
  const postLoginRedirect =
    globalThis.window.sessionStorage.getItem(POST_LOGIN_REDIRECT);
  if (postLoginRedirect) {
    console.debug("[MSAL] Redirecting to post-login path:", postLoginRedirect);
    globalThis.window.sessionStorage.removeItem(POST_LOGIN_REDIRECT);

    // Some pages need a full redirect. Check the FULL_REDIRECT_PATHS comment for deets
    if (FULL_REDIRECT_PATHS.some((rx) => rx.test(postLoginRedirect))) {
      globalThis.location.assign(
        globalThis.location.origin + postLoginRedirect,
      );
    } else {
      navigate(postLoginRedirect, { replace: true });
    }
  }
};

/**
 * Redirect to the MSAL login page for the given auth app.  This is used to log into a new account
 * in the first place, or to prompt for re-login if our token is too-far expired to refresh
 * normally.
 *
 * @param authApp - The MSAL PublicClientApplication to use for login
 * @param authConfig - The auth config to use for login (determines scopes and authority)
 * @param account - The account to login with. If undefined, will tell MS to prompt the user to
 * select an account.
 * @returns NOTHING 😜
 */
export const doLoginRedirect = async (
  authApp: PublicClientApplication,
  authConfig: AuthConfig,
  account?: AccountInfo,
) => {
  const scopes = getScopesByConfig(authConfig);
  const loginRequest: RedirectRequest = {
    scopes,
    account: account,
    // Force account selection if no active account is provided
    // This ensures the user sees the account picker instead of auto-login with cached credentials
    ...(account === undefined && { prompt: "select_account" }),
  };

  await authApp.loginRedirect(loginRequest);
};
